import React, { useState, useEffect, useRef } from "react";
import { Sparkles, Mic, Zap, ShieldCheck, LineChart } from "lucide-react";
import "./TesteHeroRotativo.css";

// 4 States for the central card
const STATES = [
  {
    id: 1,
    tag: "Inteligência",
    pillColor: "rgba(16, 185, 129, 0.15)", // green
    tagColor: "#10B981",
    title: "Copiloto",
    desc: "Categoriza e cruza dados",
    img: "/monyxx_robot_copilot_v2.png",
    icon: <Zap className="h-5 w-5" />
  },
  {
    id: 2,
    tag: "Segurança",
    pillColor: "rgba(59, 130, 246, 0.15)", // blue
    tagColor: "#3B82F6",
    title: "Guardião",
    desc: "Protege e organiza seu dinheiro",
    img: "/monyxx_robot_future_v5.png",
    icon: <ShieldCheck className="h-5 w-5" />
  },
  {
    id: 3,
    tag: "Voz",
    pillColor: "rgba(245, 158, 11, 0.15)", // yellow/orange
    tagColor: "#F59E0B",
    title: "Comando de Voz",
    desc: "Registre gastos falando, em segundos",
    img: "/monyxx_robot_voice_v2.png",
    icon: <Mic className="h-5 w-5" />
  },
  {
    id: 4,
    tag: "Investimentos",
    pillColor: "rgba(139, 92, 246, 0.15)", // purple
    tagColor: "#8B5CF6",
    title: "Carteira Inteligente",
    desc: "Ações, FIIs e Cripto em tempo real",
    img: "/monyxx_robot_cell.png",
    icon: <LineChart className="h-5 w-5" />
  }
];

export default function TesteHeroRotativo() {
  const [rawProgress, setRawProgress] = useState(0);
  const containerRef = useRef(null);
  const [currentState, setCurrentState] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [vh, setVh] = useState(window.innerHeight || 800);
  const [vw, setVw] = useState(window.innerWidth || 1200);
  const isMobile = vw < 768;

  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      // O container dá 'sticky' em top: 64px.
      // Então a animação deve começar quando rect.top chega a 64px ou menos.
      const startOffset = 64; 
      const scrolled = startOffset - rect.top;
      const currentMaxScroll = (window.innerHeight || 800) * 0.20;
      
      const newProgress = Math.min(Math.max(scrolled / currentMaxScroll, 0), 1);
      setRawProgress(newProgress);
    };

    const handleResize = () => {
      setVh(window.innerHeight);
      setVw(window.innerWidth);
      handleScroll();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);
    
    // Garantir que calcule corretamente após renderizar
    setTimeout(handleScroll, 100);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const p = 1 - Math.pow(1 - rawProgress, 3);
  
  // Usa > 0.95 para destravar o carrossel um pouco antes do final absoluto, evitando que "trave" se o usuário rolar quase tudo
  const isScrollFinished = p > 0.95;

  useEffect(() => {
    if (isHovered || !isScrollFinished) return;
    const interval = setInterval(() => {
      setCurrentState((prev) => (prev + 1) % STATES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [isHovered, isScrollFinished]);

  const handleDotClick = (index) => {
    if (index === currentState || !isScrollFinished) return;
    setCurrentState(index);
  };

  const getCardPosition = (index) => {
    if (index === currentState) return 'center';
    if (index === (currentState - 1 + STATES.length) % STATES.length) return 'left';
    if (index === (currentState + 1) % STATES.length) return 'right';
    return 'hidden';
  };

  // ----- CÁLCULOS DA SEQUÊNCIA DE ANIMAÇÃO -----
  
  // Fator de escala seguro para QUALQUER tamanho de tela (vertical e horizontal)
  // Escala mais agressiva para telas curtas para garantir que os textos do topo caibam
  const scaleY = vh < 950 ? (vh / 950) : 1;
  const scaleX = vw < 1200 ? (vw / 1200) : 1;
  const safeScale = isMobile ? (vw * 0.85 / 360) : Math.min(scaleX, scaleY);
  
  // 1. Frase Impactante Inicial
  const initialPhraseOpacity = 1 - Math.pow(p, 0.5);
  const initialPhraseTranslateY = p * -50; 

  // 2. Título Monyxx e Textos dos Cards
  const finalContentOpacity = p;
  
  // 3. Card Central: Fundo Verde Gigante que encolhe (O card é o próprio fundo verde!)
  const centerCardScale = isMobile ? 1 + (1 - p) * 1.3 : 1 + (1 - p) * 14; 
  
  // 4. Imagem do Robô: Inicia grande e reduz para 1x
  const desiredRobotScale = isMobile ? 1 + (1 - p) * 0.5 : 1 + (1 - p) * 0.8; 
  const actualRobotScale = desiredRobotScale / centerCardScale;
  
  // 5. Escala do conteúdo (textos) para anular a escala gigante do pai
  const contentScale = 1 / centerCardScale;

  // 6. Cards Laterais (Afastei de 360 para 400 para aumentar o respiro entre eles)
  const sideX = p * 400;

  return (
    <div ref={containerRef} className="teste-hero-page scroll-mode bg-[#0A1A2E] relative" style={{ overflowX: 'hidden', height: '130vh' }}>
      <div className="absolute inset-0 grid-pattern opacity-60 pointer-events-none" />

      {/* CONTAINER FIXO - Usando sticky com height correto para ficar preso enquanto o usuário rola a página */}
      <div 
        className="hero-sticky-container w-full overflow-hidden flex flex-col items-center justify-center relative"
        style={{ height: '100vh', position: 'sticky', top: '0px', perspective: '1200px' }}
      >
        
        {/* FRASE IMPACTANTE INICIAL (Posicionada na esquerda ou centro no mobile) */}
        <div 
          className={`absolute z-40 pointer-events-none flex flex-col ${isMobile ? 'items-center text-center justify-start px-4 w-full' : 'items-start justify-center'}`}
          style={{
            top: isMobile ? '12%' : '40%',
            left: isMobile ? '50%' : '3%',
            opacity: initialPhraseOpacity,
            transform: isMobile ? `translate(-50%, ${initialPhraseTranslateY}px)` : `translateY(calc(-50% + ${initialPhraseTranslateY}px))`,
            display: p === 1 ? 'none' : 'flex',
            maxWidth: isMobile ? '100%' : '35%'
          }}
        >
          <Sparkles className={`text-white opacity-80 ${isMobile ? 'h-5 w-5 mb-3' : 'h-8 w-8 mb-6'}`} />
          <h2 
            className={`font-display font-bold text-white leading-[1.2] ${isMobile ? 'text-[1.8rem]' : 'text-4xl md:text-5xl lg:text-6xl text-left'}`}
            style={{ textShadow: '0 4px 30px rgba(0,0,0,0.9)' }}
          >
            Decisões inteligentes<br />começam com o <span className="monyx-grad-text inline-block" style={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.8))' }}>Monyxx</span>.
          </h2>
        </div>

        {/* FRASE IMPACTANTE DIREITA (Nova frase) - ESCONDIDA NO MOBILE */}
        {!isMobile && (
          <div 
            className="absolute z-40 pointer-events-none flex flex-col justify-center items-end"
            style={{
              top: '65%', // Mais para baixo para equilibrar o layout
              right: '3%', // Bem na direita!
              opacity: initialPhraseOpacity,
              transform: `translateY(calc(-50% + ${initialPhraseTranslateY}px))`,
              display: p === 1 ? 'none' : 'flex',
              maxWidth: '35%' // Para não bater no robô
            }}
          >
            <Sparkles className="h-8 w-8 text-white mb-6 opacity-80" />
            <h2 
              className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white leading-tight text-right"
              style={{ textShadow: '0 4px 30px rgba(0,0,0,0.9)' }}
            >
              Inteligência <span className="monyx-grad-text inline-block" style={{ filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.8))' }}>artificial</span><br />trabalhando por você.
            </h2>
          </div>
        )}


        {/* TÍTULO FINAL - UNIFICADO NO FUNDO (Centralizado abaixo do card principal) */}
        <div 
          className="absolute z-40 flex flex-col items-center text-center w-full px-4"
          style={{ 
            bottom: isMobile ? '6%' : '-2%', // Sobe um pouco no mobile para não encostar na base
            left: '50%', 
            opacity: finalContentOpacity,
            transform: `translate(-50%, ${(1-p)*20}px)`
          }}
        >
          <h2 className={`font-display font-bold leading-tight text-white mb-1 ${isMobile ? 'text-[1.5rem]' : 'text-2xl md:text-3xl lg:text-4xl'}`}>
            Quem controla o dinheiro<br />controla o <span className="monyx-grad-text inline-block pb-1">futuro.</span>
          </h2>
          <h3 className={`font-display font-bold leading-tight text-white ${isMobile ? 'text-[1.2rem]' : 'text-xl md:text-2xl lg:text-3xl'}`}>
            O Monyxx é seu <span className="monyx-grad-text inline-block pb-1">controle.</span>
          </h3>
        </div>

        {/* WRAPPER DE ESCALA SEGURA (Apenas para os cards) */}
        <div 
          className="w-full flex flex-col items-center justify-center absolute"
          style={{ 
            top: '54%', left: '50%', 
            transform: `translate(-50%, -50%) scale(${safeScale})`, 
          }}
        >

          {/* CARDS CONTAINER */}
          <div 
            className="hero-cards-container w-full relative z-20 flex-shrink-0"
            style={{ maxWidth: '1100px', height: isMobile ? '520px' : '480px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >

            {STATES.map((state, index) => {
              const position = getCardPosition(index);
              const isActive = position === 'center';
              
              let dynamicStyle = {};
              const transitionStyle = isScrollFinished 
                ? "opacity 0.8s var(--revolut-ease), transform 0.8s var(--revolut-ease), box-shadow 0.5s ease" 
                : "none";

              if (isActive) {
                dynamicStyle = {
                  transform: isMobile ? `scale(${centerCardScale})` : `translate(-50%, -50%) scale(${centerCardScale})`,
                  borderRadius: '32px',
                  opacity: 1, 
                  transition: transitionStyle,
                  zIndex: 15, 
                  background: 'linear-gradient(135deg, #10B981 0%, #0A3266 100%)', // Fundo embutido no card
                  border: `1px solid rgba(16, 185, 129, ${0.4 * p})`,
                  boxShadow: `0 30px 60px rgba(16, 185, 129, ${0.25 * p})`,
                  overflow: 'hidden',
                  isolation: 'isolate', 
                  WebkitMaskImage: '-webkit-radial-gradient(white, black)' 
                };
              } else if (position === 'left' || position === 'right') {
                if (isMobile) {
                  dynamicStyle = { display: 'none' };
                } else {
                  dynamicStyle = {
                    transform: `translate(calc(-50% ${position === 'left' ? '-' : '+'} ${sideX}px), -50%) scale(0.85)`,
                    background: 'rgba(15, 30, 50, 0.9)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    opacity: finalContentOpacity * 0.4,
                    transition: transitionStyle,
                    zIndex: 5,
                  };
                }
              } else {
                dynamicStyle = {
                  transform: `translate(-50%, -50%) scale(0.7)`,
                  background: 'rgba(15, 30, 50, 0.9)',
                  opacity: 0,
                  transition: transitionStyle,
                  zIndex: 1,
                };
              }
              
              return (
                <div 
                  key={state.id}
                  className={`hero-card pos-${position} ${isActive ? 'active' : ''}`}
                  style={{ 
                    ...dynamicStyle, 
                    position: (isMobile && isActive) ? 'relative' : 'absolute',
                    top: (isMobile && isActive) ? 'auto' : '50%', 
                    left: (isMobile && isActive) ? 'auto' : '50%',
                  }}
                  onClick={() => handleDotClick(index)}
                >
                    <img 
                      src={state.img} 
                      alt={state.title} 
                      className="hero-card-img" 
                      style={{ 
                        inset: 0, 
                        width: '100%', height: '100%', objectFit: 'cover',
                        borderRadius: 'inherit',
                        transform: isActive ? `scale(${actualRobotScale})` : 'scale(1)',
                        transition: isScrollFinished ? "transform 0.8s ease" : "none" 
                      }}
                    />

                  <div 
                    className="hero-card-content" 
                    style={{ 
                      opacity: isActive ? finalContentOpacity : 1,
                      transform: isActive ? `scale(${contentScale})` : 'scale(1)',
                      transformOrigin: 'center center'
                    }}
                  >
                    <div className="hero-card-header">
                      <span className="hero-card-tag" style={{ color: isActive ? state.tagColor : 'rgba(255,255,255,0.8)' }}>RECURSO MONYXX</span>
                      <div className="hero-card-pill" style={isActive ? { background: state.pillColor, borderColor: state.tagColor } : {}}>
                        {state.tag}
                      </div>
                    </div>
                    <div className="hero-card-footer">
                      <h3 className="hero-card-title">{state.title}</h3>
                      <p className="hero-card-desc">{state.desc}</p>
                    </div>
                  </div>
                </div>
              );
            })}

            <div 
              className="carousel-indicators" 
              style={{ 
                opacity: finalContentOpacity, 
                transform: `translateY(${10 + (1-p)*20}px)` 
              }}
            >
              {STATES.map((_, i) => (
                <div 
                  key={i} 
                  className={`carousel-dot ${i === currentState ? 'active' : ''}`}
                  onClick={() => handleDotClick(i)}
                />
              ))}
            </div>

          </div>

        </div>

        {/* O texto inferior foi movido para o topo para criar um bloco unificado */}
      </div>
    </div>
  );
}
